import type { SubscriptionData } from './types';

/** Normalised upgrade line item from subscription catalogue/detail. */
export type UpgradableFoodType = {
  id: number;
  name: string;
  a_name?: string;
  price_per_item: number;
  max_quantity: number;
  type: 'meal' | 'snack';
};

function infer_item_type(raw: Record<string, unknown>): 'meal' | 'snack' {
  const explicit = String(raw.type ?? raw.item_type ?? '').toLowerCase();
  if (explicit.includes('snack')) {
    return 'snack';
  }
  if (explicit.includes('meal')) {
    return 'meal';
  }
  const name = String(raw.food_type_name ?? raw.meal_type_name ?? raw.name ?? raw.a_name ?? '').toLowerCase();
  if (name.includes('snack')) {
    return 'snack';
  }
  return 'meal';
}

function map_upgradable_row(raw: unknown): UpgradableFoodType | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const id = Number(row.food_type_id ?? row.meal_type_id ?? row.id ?? 0);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  const name = String(row.food_type_name ?? row.meal_type_name ?? row.name ?? '').trim();
  const a_name = row.a_name != null ? String(row.a_name) : undefined;
  const price_per_item = Number(row.price_per_item ?? 0) || 0;
  const max_quantity = Math.max(1, Number(row.maximum_selection_limit ?? row.max_quantity ?? 5) || 5);
  return {
    id,
    name: name || `Item ${id}`,
    a_name,
    price_per_item,
    max_quantity,
    type: infer_item_type(row),
  };
}

/**
 * Source of truth for upgrade options: catalogue fields on the customer subscription detail
 * (`upgradable_food_types` / `upgradable_meal_types`).
 */
export function getSubscriptionUpgradableFoodTypes(sub: SubscriptionData | null | undefined): UpgradableFoodType[] {
  if (sub == null) {
    return [];
  }
  const detail = (sub.detail ?? {}) as Record<string, unknown>;
  const subscription_detail =
    sub.subscription_detail != null && typeof sub.subscription_detail === 'object'
      ? (sub.subscription_detail as Record<string, unknown>)
      : null;

  const raw_lists = [
    detail.upgradable_food_types,
    detail.upgradable_meal_types,
    subscription_detail?.upgradable_food_types,
    subscription_detail?.upgradable_meal_types,
  ];

  const out: UpgradableFoodType[] = [];
  const seen = new Set<number>();
  for (const list of raw_lists) {
    if (!Array.isArray(list)) {
      continue;
    }
    for (const row of list) {
      const mapped = map_upgradable_row(row);
      if (mapped == null || seen.has(mapped.id)) {
        continue;
      }
      seen.add(mapped.id);
      out.push(mapped);
    }
  }
  return out;
}

/** Calendar / selectable days left for upgrade pricing (`detail.day_left`). */
export function getSubscriptionUpgradeDayLeft(sub: SubscriptionData | null | undefined): number {
  if (sub == null) {
    return 0;
  }
  const detail = (sub.detail ?? {}) as Record<string, unknown>;
  const from_detail = Number(detail.day_left);
  if (Number.isFinite(from_detail) && from_detail > 0) {
    return Math.floor(from_detail);
  }
  const nested =
    sub.subscription_detail != null && typeof sub.subscription_detail === 'object'
      ? Number((sub.subscription_detail as Record<string, unknown>).day_left)
      : NaN;
  if (Number.isFinite(nested) && nested > 0) {
    return Math.floor(nested);
  }
  return 0;
}

/**
 * Show Upgrade when the plan is upgradable, has not already been upgraded once,
 * has catalogue food/meal types, and has selectable days left.
 */
export function canUpgradeSubscription(sub: SubscriptionData | null | undefined): boolean {
  if (sub == null) {
    return false;
  }
  const detail = (sub.detail ?? {}) as Record<string, unknown>;
  const is_upgradable = detail.is_upgradable === true || detail.is_upgradable === 1 || detail.is_upgradable === '1';
  const already_upgraded =
    detail.is_subscription_upgraded === true ||
    detail.is_subscription_upgraded === 1 ||
    detail.is_subscription_upgraded === '1';
  if (!is_upgradable || already_upgraded) {
    return false;
  }
  if (getSubscriptionUpgradeDayLeft(sub) <= 0) {
    return false;
  }
  return getSubscriptionUpgradableFoodTypes(sub).length > 0;
}

/** Shape expected by POST `customer_subscriptions/upgrade` (legacy Ionic parity). */
export type UpgradeFoodTypePayloadRow = {
  food_type_id: number;
  food_type_name: string;
  a_name?: string;
  maximum_selection_limit: number;
  price_per_item: number;
  quantity: number;
};

export function buildUpgradeFoodTypesPayload(
  lines: Array<UpgradableFoodType & { quantity: number }>,
): UpgradeFoodTypePayloadRow[] {
  return lines.map(line => ({
    food_type_id: line.id,
    food_type_name: line.name,
    ...(line.a_name ? { a_name: line.a_name } : {}),
    maximum_selection_limit: line.max_quantity,
    price_per_item: line.price_per_item,
    quantity: line.quantity,
  }));
}
